import axios from 'axios';
import { newApiUrl } from '../../conf';
import {
  ApplicationAddressDTO,
  ApplicationPayload,
  ApplicationTypeDTO,
} from './dto';

export interface ApplicationsService {
  getTypes: () => Promise<ApplicationTypeDTO[]>;
  searchAddress: (query: string) => Promise<ApplicationAddressDTO[]>;
  uploadImage: (image: File, folder: string) => Promise<string>;
  /** Devuelve el código legible de la solicitud (`B-4K7QP`). */
  createApplication: (payload: ApplicationPayload) => Promise<string>;
  callbackUrl: (success: boolean, code?: string) => string;
}

const BASE = `${newApiUrl}/api/points/applications`;

/**
 * El alta de comercios aliados es pública: no usa el AxiosContext porque no hay sesión
 * ni token que adjuntar. Los endpoints de categorías y direcciones existen en el back
 * justamente porque los originales (`/api/points/types`, `/api/maps/search`) piden
 * autenticación.
 */
export const useApplicationsService = (): ApplicationsService => {
  const getTypes = async (): Promise<ApplicationTypeDTO[]> => {
    const { data } = await axios.get(`${BASE}/types`);
    return data?.data ?? [];
  };

  const searchAddress = async (
    query: string
  ): Promise<ApplicationAddressDTO[]> => {
    const { data } = await axios.get(`${BASE}/address`, { params: { query } });
    return data?.data ?? [];
  };

  // Sube contra el back y no contra file-management: ese endpoint pide credenciales y
  // el formulario no tiene sesión.
  const uploadImage = async (image: File, folder: string): Promise<string> => {
    const formData = new FormData();
    formData.append('image', image);
    formData.append('folder', folder);

    const { data } = await axios.post(`${BASE}/images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    const url = data?.data?.url;
    if (!url) throw new Error('No se pudo subir la imagen');

    return url;
  };

  const createApplication = async (
    payload: ApplicationPayload
  ): Promise<string> => {
    const { data } = await axios.post(BASE, payload);
    return data?.data?.code;
  };

  // La pantalla final la sirve el back en HTML: sobrevive a la migración de esta web.
  // Se le pasa sólo el código; el email lo resuelve el back para que no viaje en la URL.
  const callbackUrl = (success: boolean, code?: string) => {
    const params = new URLSearchParams({ status: success ? 'ok' : 'error' });
    if (code) params.set('code', code);

    return `${BASE}/callback?${params.toString()}`;
  };

  return {
    getTypes,
    searchAddress,
    uploadImage,
    createApplication,
    callbackUrl,
  };
};
